import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Dumbbell, Trophy, BarChart3, Settings, LogOut, Menu, Sparkles, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import CSPFooter from "@/components/CSPFooter";
import SeasonSelector from "@/components/SeasonSelector";
import { useVersion, formatVersion } from "@/context/VersionContext";

const STAFF = ["admin", "coach", "trainer", "secretary"];
const ALL = ["admin", "coach", "trainer", "secretary", "athlete"];

// 5 voci operative principali
const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, section: "home", roles: ["admin", "coach", "trainer"] },
  { to: "/atleti", label: "Atleti", icon: Users, section: "atleti", roles: STAFF },
  { to: "/allenamento", label: "Allenamento", icon: Dumbbell, section: "allenamento", roles: ["admin", "coach", "trainer"] },
  { to: "/gare", label: "Gare", icon: Trophy, section: "gare", roles: ALL },
  { to: "/statistiche", label: "Analisi", icon: BarChart3, section: "analisi", roles: ["admin", "coach", "trainer"] },
];

// Sub-navigation contestuale (riusa le pagine/route esistenti, nessun URL rotto)
const SUBNAV = {
  atleti: [
    { to: "/atleti", label: "Atleti", roles: STAFF },
    { to: "/gruppi", label: "Gruppi", roles: STAFF },
    { to: "/infortuni", label: "Infortuni", roles: ["admin", "coach", "trainer"] },
  ],
  allenamento: [
    { to: "/programmazione", label: "Programmazione", roles: ["admin", "coach", "trainer"] },
    { to: "/allenamento/oggi", label: "Area tecnica oggi", roles: ["admin", "coach", "trainer"] },
    { to: "/allenamento", label: "Calendario", roles: ["admin", "coach", "trainer"] },
    { to: "/presenze", label: "Presenze", roles: STAFF },
    { to: "/scherma", label: "Attività Scherma", roles: ["admin", "coach"] },
    { to: "/esercizi", label: "Esercizi", roles: ["admin", "coach", "trainer"] },
  ],
  gare: [
    { to: "/gare", label: "Calendario Gare", roles: ALL },
  ],
  analisi: [
    { to: "/statistiche", label: "Statistiche", roles: ["admin", "coach", "trainer"] },
    { to: "/report", label: "Report", roles: ["admin", "coach", "trainer", "athlete"] },
    { to: "/staff-control", label: "Staff Control", roles: ["admin", "coach", "trainer"] },
  ],
};

function sectionOf(path) {
  if (path === "/") return "home";
  if (path.startsWith("/allenamento/oggi")) return "allenamento";
  if (path.startsWith("/atleti") || path.startsWith("/gruppi") || path.startsWith("/infortuni")) return "atleti";
  if (path.startsWith("/allenamento") || path.startsWith("/programmazione") || path.startsWith("/presenze") || path.startsWith("/scherma") || path.startsWith("/esercizi")) return "allenamento";
  if (path.startsWith("/gare")) return "gare";
  if (path.startsWith("/statistiche") || path.startsWith("/report") || path.startsWith("/staff-control")) return "analisi";
  return null;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const versionInfo = useVersion();

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const section = sectionOf(location.pathname);
  const subItems = (SUBNAV[section] || []).filter((s) => !s.roles || s.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside data-testid="app-sidebar" className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 h-screen w-72 bg-white border-r border-zinc-200 flex flex-col`}>
        <div className="px-6 py-6 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="CSP" className="w-10 h-10 object-contain" />
            <div>
              <div className="font-heading font-bold text-[15px] tracking-tight leading-tight flex items-center gap-1.5">
                CSP Lab
                <span data-testid="csp2-badge" className="text-[9px] font-mono bg-black text-white rounded px-1 py-[1px] leading-none tracking-wide">2.0</span>
              </div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Technical Operating System</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 no-scrollbar">
          {items.map((n) => {
            const isActive = section === n.section;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-6 py-3 text-[15px] tap-target ${isActive ? "bg-zinc-100 text-black font-semibold border-r-2 border-black" : "text-zinc-600 hover:bg-zinc-50"}`}
              >
                <n.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-zinc-200 p-4">
          {/* Utility fuori dal menu operativo: AI Coach + Impostazioni */}
          <div className="flex items-center gap-2 mb-3">
            {["admin", "coach"].includes(user?.role) && (
              <NavLink to="/ai-coach" data-testid="util-ai-coach" onClick={() => setOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs text-zinc-600 border border-zinc-200 hover:bg-zinc-50 rounded-md">
                <Sparkles className="w-3.5 h-3.5" /> AI Coach
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink to="/impostazioni" data-testid="util-settings" onClick={() => setOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs text-zinc-600 border border-zinc-200 hover:bg-zinc-50 rounded-md">
                <Settings className="w-3.5 h-3.5" /> Impostazioni
              </NavLink>
            )}
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-zinc-700 border border-zinc-200 hover:bg-zinc-50 tap-target rounded-md"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 lg:ml-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-zinc-200 h-14 flex items-center px-4 lg:px-8">
          <button data-testid="menu-toggle" className="lg:hidden mr-3 tap-target p-2" onClick={() => setOpen(!open)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-sm">
            <img src="/logo-mark.png" alt="CSP Lab" className="w-6 h-6 object-contain" data-testid="header-logo-mark" />
            <span className="font-mono text-[13px] text-zinc-500" data-testid="app-version-badge" title={versionInfo.build_date ? `Build ${versionInfo.build_date}` : ""}>
              {formatVersion(versionInfo)}
            </span>
            {/-(dev|rc)/.test(String(versionInfo.version || "")) && <span data-testid="dev-badge" className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-[1px]">{String(versionInfo.version).includes("-rc") ? "release candidate" : "dev · preview"}</span>}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <SeasonSelector compact />
            {!online && (
              <div data-testid="offline-badge" className="flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-1 text-[11px] font-mono">
                <WifiOff className="w-3 h-3" /> Offline · sola lettura
              </div>
            )}
          </div>
        </header>

        {/* Sub-navigation contestuale */}
        {subItems.length > 0 && (
          <div data-testid="sub-nav" className="sticky top-14 z-10 bg-white border-b border-zinc-200 flex items-center gap-1 overflow-x-auto no-scrollbar px-4 lg:px-8">
            {subItems.map((s) => (
              <NavLink
                key={s.to}
                to={s.to}
                end={s.to === "/allenamento" || s.to === "/gare" || s.to === "/atleti"}
                data-testid={`subnav-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-3 text-sm border-b-2 -mb-px ${isActive ? "border-black text-black font-semibold" : "border-transparent text-zinc-500 hover:text-black"}`
                }
              >
                {s.label}
              </NavLink>
            ))}
          </div>
        )}

        <div className="p-4 lg:p-8">
          <Outlet />
          <div className="mt-12"><CSPFooter variant="app" version={versionInfo.version} /></div>
        </div>
      </main>
    </div>
  );
}
